import type { DirectiveBinding } from 'vue';

interface TooltipElement extends HTMLDivElement {
    $attrs: { [key: string]: string | number | object };
    $_tooltipValue: string;
    $_tooltipDisabled: boolean;
    $_tooltipOffset: number;
    $_tooltipShowDelay: number;
    $_tooltipHideDelay: number;
    $_tooltipModifiers?: Record<string, boolean>;
    $_mouseEnterEvent?: (event: MouseEvent) => void;
    $_tooltipPosition?: string;
}

const tooltipDirective = {
    beforeMount(el: TooltipElement, options: DirectiveBinding<string>) {
        const target = el;

        el.$_tooltipModifiers = tooltipDirective.getModifiers(options);

        if (!options.value) return;
        else if (typeof options.value === 'string') {
            target.$_tooltipValue = options.value;
            target.$_tooltipDisabled = false;
            target.$_tooltipOffset = 10;
            target.$_tooltipShowDelay = 300;
            target.$_tooltipHideDelay = 100;
        }

        tooltipDirective.bindEvents(target, options);
    },
    updated(el: TooltipElement, options: DirectiveBinding<string>) {
        const target = el;

        tooltipDirective.unbindEvents(target);

        if (!options.value) return;

        if (typeof options.value === 'string') {
            target.$_tooltipValue = options.value;

            tooltipDirective.bindEvents(target, options);
        }
    },
    unmounted(el: TooltipElement) {
        const target = el;

        tooltipDirective.remove(el);
        tooltipDirective.unbindEvents(target);
    },
    bindEvents(el: TooltipElement, options: DirectiveBinding<string>) {
        el.$_mouseEnterEvent = (event: MouseEvent) => this.onMouseEnter(event, options);
        el.addEventListener('mouseenter', el.$_mouseEnterEvent);
        el.addEventListener('mouseleave', this.onMouseLeave.bind(this));
        el.addEventListener('click', this.onClick.bind(this));
    },
    unbindEvents(el: TooltipElement) {
        if (el.$_mouseEnterEvent) {
            el.removeEventListener('mouseenter', el.$_mouseEnterEvent);
        }
        el.$_mouseEnterEvent = undefined;

        el.removeEventListener('mouseleave', this.onMouseLeave.bind(this));
        el.removeEventListener('click', this.onClick.bind(this));
    },
    onMouseEnter(event: MouseEvent, options: DirectiveBinding<string>) {
        const el = event.currentTarget as TooltipElement;

        tooltipDirective.show(el, options);
    },
    onMouseLeave(event: MouseEvent) {
        const el = event.currentTarget as TooltipElement;
        const hideDelay = el.$_tooltipHideDelay ?? 200;

        tooltipDirective.hide(el, hideDelay);
    },
    onClick() {},
    getModifiers(binding: DirectiveBinding<string>) {
        return binding.modifiers;
    },
    tooltipActions(el: TooltipElement, options: DirectiveBinding<string>) {
        // Should check if the element exists and if the tooltip is disabled
        if (el.$_tooltipDisabled || !isExist(el)) return;

        const tooltipElement = tooltipDirective.createTooltipElement(el, options);

        tooltipDirective.positionTooltip(el);

        if (tooltipElement) {
            tooltipElement.style.opacity = '1';
        }
    },
    positionTooltip(el: TooltipElement) {
        const modifiers = el.$_tooltipModifiers;

        if (!modifiers) return;

        if (modifiers.top) {
            this.alignTop(el);

            if (this.isOutOfBounds()) {
                this.alignBottom(el);

                if (this.isOutOfBounds()) {
                    this.alignLeft(el);
                }

                if (this.isOutOfBounds()) {
                    this.alignTop(el);
                }
            }
        } else if (modifiers.left) {
            this.alignLeft(el);

            if (this.isOutOfBounds()) {
                this.alignRight(el);

                if (this.isOutOfBounds()) {
                    this.alignTop(el);

                    if (this.isOutOfBounds()) {
                        this.alignBottom(el);

                        if (this.isOutOfBounds()) {
                            this.alignLeft(el);
                        }
                    }
                }
            }
        } else if (modifiers.bottom) {
            this.alignBottom(el);

            if (this.isOutOfBounds()) {
                this.alignTop(el);

                if (this.isOutOfBounds()) {
                    this.alignRight(el);

                    if (this.isOutOfBounds()) {
                        this.alignLeft(el);
                    }
                }
            }
        } else {
            this.alignRight(el);

            if (this.isOutOfBounds()) {
                this.alignLeft(el);

                if (this.isOutOfBounds()) {
                    this.alignTop(el);

                    if (this.isOutOfBounds()) {
                        this.alignBottom(el);

                        if (this.isOutOfBounds()) {
                            this.alignRight(el);
                        }
                    }
                }
            }
        }
    },
    isOutOfBounds() {
        const tooltipElement = tooltipDirective.getTooltipElement();

        if (!tooltipElement) return;
        const offset = tooltipElement.getBoundingClientRect();
        const targetTop = offset.top;
        const targetLeft = offset.left;
        const width = getOuterWidth(tooltipElement);
        const height = getOuterHeight(tooltipElement);
        const viewport = getViewport();

        return (
            targetLeft + width > viewport.width ||
            targetLeft < 0 ||
            targetTop < 0 ||
            targetTop + height > viewport.height
        );
    },
    getHostOffset(el: TooltipElement) {
        const offset = el.getBoundingClientRect();
        const targetLeft = offset.left + getWindowScrollLeft();
        const targetTop = offset.top + getWindowScrollTop();

        return { left: targetLeft, top: targetTop };
    },
    preAlign(el: TooltipElement, position: string) {
        const tooltipElement = this.getTooltipElement();

        if (!tooltipElement) return;
        tooltipElement.style.left = -999 + 'px';
        tooltipElement.style.top = -999 + 'px';
        removeClass(tooltipElement, `mod-${tooltipElement.$_tooltipPosition}`);
        addClass(tooltipElement, `mod-${position}`);

        tooltipElement.$_tooltipPosition = position;
        tooltipElement.setAttribute('data-vz-position', position);

        const arrowElement = findSingle(
            tooltipElement,
            '[data-vzc-section="arrow"]',
        ) as HTMLElement | null;
        const offset = 10;
        el.$_tooltipOffset = offset;

        const elementPosition = el.getBoundingClientRect();

        if (!arrowElement) return;

        switch (position) {
            case 'right':
                tooltipElement.style.left =
                    offset + elementPosition.x + elementPosition.width + 'px';
                tooltipElement.style.top = elementPosition.y + elementPosition.height / 2 + 'px';
                tooltipElement.style.transform = 'translateY(-50%)';
                break;
            case 'left':
                tooltipElement.style.right = -offset + elementPosition.x + 'px';
                tooltipElement.style.top = elementPosition.y + 'px';
                tooltipElement.style.transform = 'translateY(-50%)';
                break;
            case 'bottom':
                tooltipElement.style.top =
                    offset + elementPosition.y + elementPosition.height + 'px';
                tooltipElement.style.left = elementPosition.x + elementPosition.width / 2 + 'px';
                tooltipElement.style.transform = 'translateX(-50%)';
                break;
            case 'top':
            default:
                tooltipElement.style.top = -offset + elementPosition.y + 'px';
                tooltipElement.style.left = elementPosition.x + elementPosition.width / 2 + 'px';
                tooltipElement.style.transform = 'translateX(-50%)';
        }
    },
    alignRight(el: TooltipElement) {
        tooltipDirective.preAlign(el, 'right');
        const tooltipElement = this.getTooltipElement();
        if (!tooltipElement) return;

        const hostOffset = this.getHostOffset(el);
        const left = hostOffset.left + getOuterWidth(el);
        const top = hostOffset.top + getOuterHeight(el) / 2;

        tooltipElement.style.left = el.$_tooltipOffset + left + 'px';
        tooltipElement.style.top = top + 'px';
    },
    alignLeft(el: TooltipElement) {
        this.preAlign(el, 'left');
        const tooltipElement = this.getTooltipElement();
        if (!tooltipElement) return;

        const hostOffset = this.getHostOffset(el);
        const left = -el.$_tooltipOffset + hostOffset.left - getOuterWidth(tooltipElement);
        const top = hostOffset.top + getOuterHeight(el) / 2;

        tooltipElement.style.left = left + 'px';
        tooltipElement.style.top = top + 'px';
    },
    alignTop(el: TooltipElement) {
        this.preAlign(el, 'top');
        const tooltipElement = this.getTooltipElement();
        if (!tooltipElement) return;

        const hostOffset = this.getHostOffset(el);
        const left = hostOffset.left + getOuterWidth(el) / 2;
        const top = -el.$_tooltipOffset + hostOffset.top - getOuterHeight(tooltipElement);

        tooltipElement.style.left = left + 'px';
        tooltipElement.style.top = top + 'px';
    },
    alignBottom(el: TooltipElement) {
        this.preAlign(el, 'bottom');
        const tooltipElement = this.getTooltipElement();
        if (!tooltipElement) return;

        const hostOffset = this.getHostOffset(el);
        const left = hostOffset.left + getOuterWidth(el) / 2;
        const top = el.$_tooltipOffset + hostOffset.top + getOuterHeight(el);

        tooltipElement.style.left = left + 'px';
        tooltipElement.style.top = top + 'px';
    },
    createTooltipElement(el: TooltipElement, options: DirectiveBinding<string>) {
        const modifiers = tooltipDirective.getModifiers(options);

        let tooltipClass = 'tooltip';

        const tooltipArrow = createElement('div', {
            class: 'tooltip-arrow',
        });

        const tooltipContent = createElement('div', {
            class: 'tooltip-content',
        });

        if (tooltipArrow && tooltipContent) {
            tooltipContent.innerHTML = el.$_tooltipValue;
            tooltipContent.appendChild(tooltipArrow);
        }

        if (modifiers.wide) {
            tooltipClass += ' mod-wide';
        }

        if (modifiers.error) {
            tooltipClass += ' mod-error';
        }

        if (tooltipArrow && tooltipContent) {
            const tooltipContainer = createElement(
                'div',
                {
                    id: 'tooltip-container',
                    role: 'tooltip',
                    style: {
                        display: 'inline-block',
                        width: 'fit-content',
                        pointerEvents: 'none',
                    },
                    class: tooltipClass,
                },
                tooltipArrow,
                tooltipContent,
            );

            if (tooltipContainer) {
                document.body.appendChild(tooltipContainer);
            }

            return tooltipContainer;
        }
    },
    show(el: TooltipElement, options: DirectiveBinding<string>, showDelay = 500) {
        tooltipDirective.timer = setTimeout(
            () => tooltipDirective.tooltipActions(el, options),
            showDelay,
        );
    },
    hide(el: TooltipElement, hideDelay?: number) {
        clearTimeout(tooltipDirective.timer);

        if (hideDelay !== undefined) {
            setTimeout(() => tooltipDirective.tooltipRemoval(el), hideDelay);
        } else {
            tooltipDirective.tooltipRemoval(el);
        }
    },
    tooltipRemoval(el: TooltipElement) {
        tooltipDirective.remove(el);
    },
    remove(el: TooltipElement) {
        if (!el) return;

        const tooltipElement = tooltipDirective.getTooltipElement();

        if (tooltipElement?.parentElement) {
            tooltipElement.parentElement.removeChild(tooltipElement);
        }
    },
    getTooltipElement() {
        return document.getElementById('tooltip-container') as TooltipElement;
    },
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
};
export default tooltipDirective;

export function getParentNode(element: Node): ParentNode | null {
    if (element) {
        let parent = element.parentNode;

        if (parent && parent instanceof ShadowRoot && parent.host) {
            parent = parent.host;
        }

        return parent;
    }

    return null;
}

export function isExist(element: Node): boolean {
    return !!(element?.nodeName && getParentNode(element));
}

export function createElement(
    type: string,
    attributes: { [key: string]: string | number | object } = {},
    ...children: (string | Node)[]
): HTMLElement | undefined {
    if (type) {
        const element = document.createElement(type) as TooltipElement;

        setAttributes(element, attributes);
        element.append(...children);

        return element;
    }

    return undefined;
}
export function isElement(element: TooltipElement): boolean {
    return typeof HTMLElement === 'object'
        ? element instanceof HTMLElement
        : element &&
              typeof element === 'object' &&
              element !== null &&
              element.nodeType === 1 &&
              typeof element.nodeName === 'string';
}

export function setAttributes(
    element: TooltipElement,
    attributes: { [key: string]: string | number | object } = {},
): void {
    if (isElement(element)) {
        const computedStyles = (rule: string, value: string | number | object): string[] => {
            const styles = element?.$attrs?.[rule] ? [element?.$attrs?.[rule]] : [];

            return [value]
                .flatMap((v) => (Array.isArray(v) ? v : [v]))
                .reduce((cv, v) => {
                    if (v !== null && v !== undefined) {
                        const type = typeof v;

                        if (type === 'string' || type === 'number') {
                            cv.push(v);
                        } else if (type === 'object') {
                            const _cv = Array.isArray(v)
                                ? computedStyles(rule, v)
                                : Object.entries(v).map(([_k, _v]) =>
                                      rule === 'style' && (!!_v || _v === 0)
                                          ? `${_k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}:${_v}`
                                          : _v
                                            ? _k
                                            : undefined,
                                  );

                            cv = _cv.length ? cv.concat(_cv.filter((c) => !!c)) : cv;
                        }
                    }

                    return cv;
                }, styles);
        };

        Object.entries(attributes).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                const matchedEvent = key.match(/^on(.+)/);

                if (matchedEvent) {
                    element.addEventListener(
                        matchedEvent[1].toLowerCase(),
                        value as unknown as EventListener,
                    );
                } else {
                    value =
                        key === 'class'
                            ? [...new Set(computedStyles('class', value))].join(' ').trim()
                            : key === 'style'
                              ? computedStyles('style', value).join(';').trim()
                              : value;
                    element.$attrs = element.$attrs || {};
                    element.$attrs[key] = value;
                    element.setAttribute(key, value as string);
                }
            }
        });
    }
}

export function getViewport(): { width: number; height: number } {
    const win = window,
        d = document,
        e = d.documentElement,
        g = d.getElementsByTagName('body')[0],
        w = win.innerWidth || e.clientWidth || g.clientWidth,
        h = win.innerHeight || e.clientHeight || g.clientHeight;

    return { width: w, height: h };
}

export function getOuterHeight(element: HTMLElement, margin?: boolean): number {
    if (element) {
        let height = element.offsetHeight;

        if (margin) {
            const style = getComputedStyle(element);

            height += parseFloat(style.marginTop) + parseFloat(style.marginBottom);
        }

        return height;
    }

    return 0;
}

export function getOuterWidth(element: unknown, margin?: boolean): number {
    if (element instanceof HTMLElement) {
        let width = element.offsetWidth;

        if (margin) {
            const style = getComputedStyle(element);

            width += parseFloat(style.marginLeft) + parseFloat(style.marginRight);
        }

        return width;
    }

    return 0;
}

export function getWindowScrollLeft(): number {
    const doc = document.documentElement;

    return (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
}

export function getWindowScrollTop(): number {
    const doc = document.documentElement;

    return (window.scrollY || doc.scrollTop) - (doc.clientTop || 0);
}

export function removeClass(element: Element, className: string | string[]): void {
    if (element && className) {
        const fn = (_className: string) => {
            if (element.classList) element.classList.remove(_className);
            else
                element.className = element.className.replace(
                    new RegExp('(^|\\b)' + _className.split(' ').join('|') + '(\\b|$)', 'gi'),
                    ' ',
                );
        };

        [className]
            .flat()
            .filter(Boolean)
            .forEach((_classNames) => _classNames.split(' ').forEach(fn));
    }
}

export function addClass(element: Element, className: string | string[]): void {
    if (element && className) {
        const fn = (_className: string) => {
            if (!hasClass(element, _className)) {
                if (element.classList) element.classList.add(_className);
                else element.className += ' ' + _className;
            }
        };

        [className]
            .flat()
            .filter(Boolean)
            .forEach((_classNames) => _classNames.split(' ').forEach(fn));
    }
}

export function hasClass(element: Element, className: string): boolean {
    if (element) {
        if (element.classList) return element.classList.contains(className);
        else return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
    }

    return false;
}

export function findSingle(element: TooltipElement, selector: string): TooltipElement | null {
    return isElement(element)
        ? element.matches(selector)
            ? element
            : element.querySelector(selector)
        : null;
}
